import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { AdminModal, BTN_BLACK, BTN_WHITE, errText } from "@/components/pageSettings/AdminModal";
import { EMPTY_NOTIFY, NotifyBuyerBox, notifyParams } from "@/components/NotifyBuyerBox";

function Check({ checked, onChange, label, hint, testId, disabled = false }) {
  return (
    <label className={`flex items-start gap-2 border border-zinc-300 px-2 py-1.5 text-sm ${disabled ? "opacity-60" : "cursor-pointer hover:border-black"}`}>
      <input type="checkbox" className="mt-0.5 accent-black" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} data-testid={testId} />
      <span>{label}{hint && <span className="block text-[10.5px] text-zinc-600">{hint}</span>}</span>
    </label>
  );
}

function ExistingPages({ landers }) {
  if (!landers.length) return null;
  return (
    <div className="mt-4" data-testid="build-pages-existing">
      <div className="label-mono mb-1">Already built ({landers.length})</div>
      <ul className="divide-y divide-zinc-200 border border-zinc-300 text-xs">
        {landers.map((l) => (
          <li key={l.id} className="flex items-center gap-2 px-2 py-1.5">
            <span className="font-mono">/lp/{l.slug}</span>
            <span className="text-zinc-500">{l.status}</span>
            <Link to={`/admin/landing-pages/custom/${l.id}`} className="ml-auto underline" data-testid={`build-pages-edit-${l.slug}`}>Edit</Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Build every page for one buyer at once: one checklist, everything ticked, untick what you don't want. */
export function BuildPagesDialog({ b, onClose, onBuilt }) {
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState({});
  const [thankyou, setThankyou] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notify, setNotify] = useState(EMPTY_NOTIFY);
  const load = () => Promise.all([api.get("/admin/lander-templates"), api.get(`/admin/buyers/${b.id}/landers`)])
    .then(([t, l]) => {
      const built = new Set(l.data.landers.map((x) => x.template_funnel));
      setPicked(Object.fromEntries(t.data.funnels.map((f) => [f.key, !built.has(f.key)])));
      setThankyou(!l.data.thankyou_custom);
      setData({ funnels: t.data.funnels, ...l.data });
    });
  useEffect(() => { load().catch((e) => toast.error(errText(e, "Couldn't load templates"))); }, [b.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const templates = Object.keys(picked).filter((k) => picked[k]);
  const build = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/admin/buyers/${b.id}/landers`, { templates, thankyou: thankyou && !data.thankyou_custom }, notifyParams(notify));
      setNotify(EMPTY_NOTIFY);
      toast.success(`Built ${r.created.length} page${r.created.length === 1 ? "" : "s"} as drafts${r.thankyou_set ? " and a thank-you page" : ""}`);
      await load();
      onBuilt?.();
    } catch (e) {
      toast.error(errText(e, "Couldn't build the pages"));
    } finally {
      setBusy(false);
    }
  };
  const footer = (
    <>
      <button type="button" className={BTN_WHITE} onClick={onClose} data-testid="build-pages-cancel">Close</button>
      <button type="button" className={BTN_BLACK} onClick={build} disabled={busy || !data || (!templates.length && !(thankyou && !data.thankyou_custom))} data-testid="build-pages-submit">
        {busy ? "Building…" : `Build ${templates.length} page${templates.length === 1 ? "" : "s"}`}
      </button>
    </>
  );
  return (
    <AdminModal eyebrow="Build all pages" title={b.firm_name} onClose={onClose} footer={footer} testId="build-pages-dialog">
      {!data ? <div className="label-mono">Loading…</div> : (
        <>
          <p className="mb-3 text-xs text-zinc-700">
            Each page starts as a draft copy of the shared page, branded for {b.firm_name}. Leads that fit {b.firm_name} go
            to them first; if they can&apos;t take it, the next matching buyer gets it and the visitor sees that buyer&apos;s
            thank-you page.
          </p>
          <div className="grid gap-1.5 sm:grid-cols-2" data-testid="build-pages-checklist">
            {data.funnels.map((f) => (
              <Check key={f.key} checked={Boolean(picked[f.key])} onChange={(v) => setPicked((s) => ({ ...s, [f.key]: v }))}
                label={`${f.title} landing page`} testId={`build-pages-pick-${f.key}`}
                hint={data.landers.some((l) => l.template_funnel === f.key) ? "Already built once; ticking makes another copy" : null} />
            ))}
            <Check checked={thankyou && !data.thankyou_custom} onChange={setThankyou} disabled={data.thankyou_custom}
              label="Thank-you page" testId="build-pages-pick-thankyou"
              hint={data.thankyou_custom ? "Already has its own thank-you page; we'll keep it" : "Starts with their firm name and next steps; edit it any time"} />
          </div>
          <div className="mt-3"><NotifyBuyerBox value={notify} onChange={setNotify} testId="build-pages-notify"
            preview={() => ({ kind: "own_pages", page_id: b.id, templates })} /></div>
          <ExistingPages landers={data.landers} />
        </>
      )}
    </AdminModal>
  );
}
